import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package, Heart, MessageSquare, Building2, Eye, CheckCircle, XCircle,
  Clock, Send, RefreshCw, Search, AlertTriangle, TrendingUp, Users, Loader2,
  Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const PEDIDO_STATUS_CONFIG = {
  'Pendente': { color: 'bg-amber-500', label: 'Pendente' },
  'Aprovado': { color: 'bg-green-500', label: 'Aprovado' },
  'Em_Transito': { color: 'bg-blue-500', label: 'Em Trânsito' },
  'Concluido': { color: 'bg-slate-500', label: 'Concluído' },
  'Rejeitado': { color: 'bg-red-500', label: 'Rejeitado' }
};

const URGENCIA_CONFIG = {
  'Alta': { color: 'text-red-600 bg-red-50', label: 'Alta' },
  'Media': { color: 'text-amber-600 bg-amber-50', label: 'Média' },
  'Baixa': { color: 'text-green-600 bg-green-50', label: 'Baixa' }
};

const MISSAO_STATUS_CONFIG = {
  'Aberta': { color: 'bg-green-500', label: 'Aberta' },
  'Em_Curso': { color: 'bg-blue-500', label: 'Em Curso' },
  'Concluida': { color: 'bg-slate-500', label: 'Concluída' },
  'Cancelada': { color: 'bg-red-500', label: 'Cancelada' }
};

const MENSAGEM_STATUS_CONFIG = {
  'Aberto': { color: 'bg-amber-500', label: 'Aberto' },
  'Respondido': { color: 'bg-blue-500', label: 'Respondido' },
  'Fechado': { color: 'bg-slate-500', label: 'Fechado' }
};

export default function AdminPortalPage() {
  const [activeTab, setActiveTab] = useState('pedidos');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pedidos_pendentes: 0,
    pedidos_total: 0,
    missoes_abertas: 0,
    mensagens_abertas: 0,
    candidaturas_pendentes: 0
  });
  const [pedidos, setPedidos] = useState([]);
  const [missoes, setMissoes] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  
  // Filters
  const [filtroStatusPedido, setFiltroStatusPedido] = useState('all');
  const [filtroStatusMissao, setFiltroStatusMissao] = useState('all');
  const [filtroStatusMensagem, setFiltroStatusMensagem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [selectedMissao, setSelectedMissao] = useState(null);
  const [selectedMensagem, setSelectedMensagem] = useState(null);
  const [resposta, setResposta] = useState('');
  const [novoStatus, setNovoStatus] = useState('');
  const [notasAdmin, setNotasAdmin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, pedidosRes, missoesRes, mensagensRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/stats`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/pedidos`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/missoes`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/mensagens`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (pedidosRes.ok) setPedidos(await pedidosRes.json());
      if (missoesRes.ok) setMissoes(await missoesRes.json());
      if (mensagensRes.ok) setMensagens(await mensagensRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openPedidoDetail = async (pedido) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/pedidos/${pedido.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedPedido(data);
        setNovoStatus(data.status);
        setNotasAdmin(data.notas_admin || '');
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleUpdatePedidoStatus = async () => {
    if (!novoStatus) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/pedidos/${selectedPedido.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: novoStatus, notas_admin: notasAdmin })
      });
      
      if (response.ok) {
        toast.success(`Pedido atualizado para ${novoStatus}`);
        setSelectedPedido(null);
        loadData();
      } else {
        toast.error('Erro ao atualizar pedido');
      }
    } catch (error) {
      toast.error('Erro ao atualizar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const openMensagemDetail = async (mensagem) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/mensagens/${mensagem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedMensagem(data);
        setResposta('');
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleResponderMensagem = async () => {
    if (!resposta.trim()) {
      toast.error('Escreva uma resposta');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/mensagens/${selectedMensagem.id}/responder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mensagem: resposta })
      });
      
      if (response.ok) {
        toast.success('Resposta enviada!');
        setResposta('');
        openMensagemDetail(selectedMensagem); // Reload
        loadData();
      }
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFecharMensagem = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/mensagens/${selectedMensagem.id}/fechar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Ticket fechado');
        setSelectedMensagem(null);
        loadData();
      }
    } catch (error) {
      toast.error('Erro ao fechar ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteConfirm = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete || !deleteType) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (deleteType === 'pedido') {
        endpoint = `/api/admin/portal/pedidos/${itemToDelete.id}`;
      } else if (deleteType === 'mensagem') {
        endpoint = `/api/admin/portal/mensagens/${itemToDelete.id}`;
      } else if (deleteType === 'missao') {
        endpoint = `/api/admin/missoes/${itemToDelete.id}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`${deleteType === 'pedido' ? 'Pedido' : deleteType === 'mensagem' ? 'Ticket' : 'Missão'} eliminado com sucesso`);
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
        setDeleteType(null);
        loadData();
      } else {
        toast.error('Erro ao eliminar');
      }
    } catch (error) {
      toast.error('Erro ao eliminar');
    } finally {
      setSubmitting(false);
    }
  };

  const openMissaoDetail = async (missao) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/missoes/${missao.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedMissao(data);
        setNovoStatus(data.status);
        setNotasAdmin(data.notas_admin || '');
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleUpdateMissaoStatus = async () => {
    if (!novoStatus) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/missoes/${selectedMissao.id}/status?status=${novoStatus}&notas_admin=${encodeURIComponent(notasAdmin || '')}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(`Missão atualizada para ${novoStatus}`);
        const updated = await response.json();
        setSelectedMissao(updated);
        loadData();
      } else {
        toast.error('Erro ao atualizar missão');
      }
    } catch (error) {
      toast.error('Erro ao atualizar missão');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAceitarCandidatura = async (candidaturaId) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/missoes/${selectedMissao.id}/candidaturas/${candidaturaId}/aceitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Candidatura aceite com sucesso');
        openMissaoDetail(selectedMissao);
      } else {
        toast.error('Erro ao aceitar candidatura');
      }
    } catch (error) {
      toast.error('Erro ao aceitar candidatura');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejeitarCandidatura = async (candidaturaId) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/missoes/${selectedMissao.id}/candidaturas/${candidaturaId}/rejeitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Candidatura rejeitada');
        openMissaoDetail(selectedMissao);
      } else {
        toast.error('Erro ao rejeitar candidatura');
      }
    } catch (error) {
      toast.error('Erro ao rejeitar candidatura');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered data
  const filteredPedidos = pedidos.filter(p => {
    if (filtroStatusPedido !== 'all' && p.status !== filtroStatusPedido) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return p.codigo?.toLowerCase().includes(search) ||
             p.instituicao_nome?.toLowerCase().includes(search);
    }
    return true;
  });

  const filteredMissoes = missoes.filter(m => {
    if (filtroStatusMissao !== 'all' && m.status !== filtroStatusMissao) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return m.codigo?.toLowerCase().includes(search) ||
             m.titulo?.toLowerCase().includes(search) ||
             m.instituicao_nome?.toLowerCase().includes(search);
    }
    return true;
  });

  const filteredMensagens = mensagens.filter(m => {
    if (filtroStatusMensagem !== 'all' && m.status !== filtroStatusMensagem) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return m.codigo?.toLowerCase().includes(search) ||
             m.assunto?.toLowerCase().includes(search) ||
             m.instituicao_nome?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-portal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-[#0066CC]" />
            Gestão de Pedidos
          </h1>
          <p className="text-slate-500">Gerir pedidos, missões e tickets submetidos pelas instituições</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`${stats.pedidos_pendentes > 0 ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' : ''}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.pedidos_pendentes}</p>
                <p className="text-sm text-slate-500">Pedidos Pendentes</p>
              </div>
              <Package className="w-8 h-8 text-amber-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.missoes_abertas}</p>
                <p className="text-sm text-slate-500">Missões Abertas</p>
              </div>
              <Heart className="w-8 h-8 text-pink-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card className={`${stats.mensagens_abertas > 0 ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' : ''}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.mensagens_abertas}</p>
                <p className="text-sm text-slate-500">Tickets Abertos</p>
              </div>
              <MessageSquare className="w-8 h-8 text-blue-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-purple-600">{stats.candidaturas_pendentes}</p>
                <p className="text-sm text-slate-500">Candidaturas Vol.</p>
              </div>
              <Users className="w-8 h-8 text-purple-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pedidos" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Pedidos ({filteredPedidos.length})
          </TabsTrigger>
          <TabsTrigger value="missoes" className="flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Missões ({filteredMissoes.length})
          </TabsTrigger>
          <TabsTrigger value="mensagens" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Tickets ({filteredMensagens.length})
          </TabsTrigger>
        </TabsList>

        {/* Pedidos Tab */}
        <TabsContent value="pedidos" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar por código ou instituição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filtroStatusPedido} onValueChange={setFiltroStatusPedido}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Status</SelectItem>
                    {Object.entries(PEDIDO_STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Código</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Urgência</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPedidos.map(pedido => {
                    const statusConfig = PEDIDO_STATUS_CONFIG[pedido.status] || PEDIDO_STATUS_CONFIG['Pendente'];
                    const urgConfig = URGENCIA_CONFIG[pedido.urgencia] || URGENCIA_CONFIG['Media'];
                    return (
                      <TableRow key={pedido.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openPedidoDetail(pedido)}>
                        <TableCell className="font-mono font-medium">{pedido.codigo}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{pedido.instituicao_nome}</TableCell>
                        <TableCell>{pedido.tipo_recurso}</TableCell>
                        <TableCell>
                          <Badge className={urgConfig.color}>{urgConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(pedido.created_at).toLocaleDateString('pt-PT')}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openPedidoDetail(pedido)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDeleteConfirm(pedido, 'pedido'); }}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredPedidos.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhum pedido encontrado</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Missões Tab */}
        <TabsContent value="missoes" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar por código, título ou instituição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filtroStatusMissao} onValueChange={setFiltroStatusMissao}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Status</SelectItem>
                    {Object.entries(MISSAO_STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Código</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Voluntários</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMissoes.map(missao => {
                    const statusConfig = MISSAO_STATUS_CONFIG[missao.status] || MISSAO_STATUS_CONFIG['Aberta'];
                    return (
                      <TableRow key={missao.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openMissaoDetail(missao)}>
                        <TableCell className="font-mono font-medium">{missao.codigo}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{missao.titulo}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{missao.instituicao_nome}</TableCell>
                        <TableCell>{missao.tipo_missao}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            {missao.voluntarios_confirmados || 0}/{missao.numero_voluntarios}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredMissoes.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhuma missão encontrada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mensagens Tab */}
        <TabsContent value="mensagens" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar por código ou assunto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filtroStatusMensagem} onValueChange={setFiltroStatusMensagem}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Status</SelectItem>
                    {Object.entries(MENSAGEM_STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Código</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMensagens.map(mensagem => {
                    const statusConfig = MENSAGEM_STATUS_CONFIG[mensagem.status] || MENSAGEM_STATUS_CONFIG['Aberto'];
                    return (
                      <TableRow key={mensagem.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openMensagemDetail(mensagem)}>
                        <TableCell className="font-mono font-medium">{mensagem.codigo}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{mensagem.assunto}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{mensagem.instituicao_nome}</TableCell>
                        <TableCell>{mensagem.categoria}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(mensagem.created_at).toLocaleDateString('pt-PT')}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openMensagemDetail(mensagem)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDeleteConfirm(mensagem, 'mensagem'); }}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredMensagens.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhuma mensagem encontrada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmar Eliminação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja eliminar este {deleteType === 'pedido' ? 'pedido' : deleteType === 'mensagem' ? 'ticket' : 'item'}?
              {itemToDelete && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{itemToDelete.codigo}</p>
                  <p className="text-sm text-slate-500">
                    {itemToDelete.instituicao_nome}
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteItem} disabled={submitting}>
              {submitting ? 'A eliminar...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pedido Detail Modal */}
      <Dialog open={!!selectedPedido} onOpenChange={() => setSelectedPedido(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Pedido {selectedPedido?.codigo}</span>
              {selectedPedido && (
                <Badge className={`${PEDIDO_STATUS_CONFIG[selectedPedido.status]?.color || 'bg-slate-500'} text-white`}>
                  {PEDIDO_STATUS_CONFIG[selectedPedido.status]?.label || selectedPedido.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedPedido?.instituicao?.nome || selectedPedido?.instituicao_nome}
            </DialogDescription>
          </DialogHeader>

          {selectedPedido && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Tipo Recurso</Label>
                  <p className="font-medium">{selectedPedido.tipo_recurso}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Urgência</Label>
                  <Badge className={URGENCIA_CONFIG[selectedPedido.urgencia]?.color}>
                    {selectedPedido.urgencia}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Quantidade</Label>
                  <p className="font-medium">{selectedPedido.quantidade} {selectedPedido.unidade}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Destinatário</Label>
                  <p className="font-medium">{selectedPedido.destinatario}</p>
                </div>
              </div>

              {selectedPedido.justificacao && (
                <div>
                  <Label className="text-xs text-slate-500">Justificação</Label>
                  <p className="text-sm p-3 bg-slate-50 rounded-lg mt-1">{selectedPedido.justificacao}</p>
                </div>
              )}

              {/* Timeline */}
              {selectedPedido.timeline?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Histórico</Label>
                  <div className="space-y-2">
                    {selectedPedido.timeline.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className={`w-2 h-2 mt-2 rounded-full ${PEDIDO_STATUS_CONFIG[entry.status]?.color || 'bg-slate-400'}`}></div>
                        <div>
                          <p className="font-medium">{entry.status}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(entry.data).toLocaleString('pt-PT')} - {entry.user_nome}
                          </p>
                          {entry.notas && <p className="text-xs text-slate-600 italic">{entry.notas}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Update Status */}
              {selectedPedido.status !== 'Concluido' && selectedPedido.status !== 'Rejeitado' && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Atualizar Status</Label>
                  <Select value={novoStatus} onValueChange={setNovoStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PEDIDO_STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="text-xs text-slate-500">Notas (opcional)</Label>
                    <Textarea
                      value={notasAdmin}
                      onChange={(e) => setNotasAdmin(e.target.value)}
                      placeholder="Adicionar notas sobre esta atualização..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedPedido(null)}>Fechar</Button>
            {selectedPedido?.status !== 'Concluido' && selectedPedido?.status !== 'Rejeitado' && (
              <Button onClick={handleUpdatePedidoStatus} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Atualizar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Missão Detail Modal */}
      <Dialog open={!!selectedMissao} onOpenChange={() => setSelectedMissao(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedMissao?.codigo}</span>
              {selectedMissao && (
                <Badge className={`${MISSAO_STATUS_CONFIG[selectedMissao.status]?.color || 'bg-slate-500'} text-white`}>
                  {MISSAO_STATUS_CONFIG[selectedMissao.status]?.label || selectedMissao.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedMissao && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedMissao.titulo}</h3>
                <p className="text-sm text-slate-500">{selectedMissao.instituicao?.nome || selectedMissao.instituicao_nome}</p>
              </div>
              <p className="text-slate-600">{selectedMissao.descricao}</p>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">Tipo</Label>
                  <p>{selectedMissao.tipo_missao}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Voluntários</Label>
                  <p>{selectedMissao.candidaturas?.filter(c => c.status === 'Confirmado').length || 0} / {selectedMissao.numero_voluntarios}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Data</Label>
                  <p>{new Date(selectedMissao.data_inicio).toLocaleDateString('pt-PT')}</p>
                </div>
                {selectedMissao.horario && (
                  <div>
                    <Label className="text-xs text-slate-500">Horário</Label>
                    <p>{selectedMissao.horario}</p>
                  </div>
                )}
              </div>

              {selectedMissao.candidaturas?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Candidaturas ({selectedMissao.candidaturas.length})</Label>
                  <div className="space-y-2">
                    {selectedMissao.candidaturas.map(cand => (
                      <div key={cand.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{cand.voluntario?.nome || 'Voluntário'}</p>
                          <p className="text-xs text-slate-500">{cand.voluntario?.email} • {cand.voluntario?.telefone}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {cand.status === 'Pendente' ? (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleAceitarCandidatura(cand.id)}
                                disabled={submitting}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleRejeitarCandidatura(cand.id)}
                                disabled={submitting}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <Badge className={cand.status === 'Aceite' || cand.status === 'Confirmado' ? 'bg-green-500' : cand.status === 'Rejeitada' ? 'bg-red-500' : 'bg-amber-500'}>
                              {cand.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Update Status */}
              {selectedMissao.status !== 'Concluída' && selectedMissao.status !== 'Cancelada' && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Atualizar Status da Missão</Label>
                  <Select value={novoStatus} onValueChange={setNovoStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aberta">Aberta</SelectItem>
                      <SelectItem value="Em Curso">Em Curso</SelectItem>
                      <SelectItem value="Concluída">Concluída</SelectItem>
                      <SelectItem value="Cancelada">Cancelada</SelectItem>
                      <SelectItem value="Suspensa">Suspensa</SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="text-xs text-slate-500">Notas (opcional)</Label>
                    <Textarea
                      value={notasAdmin}
                      onChange={(e) => setNotasAdmin(e.target.value)}
                      placeholder="Adicionar notas sobre esta atualização..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedMissao(null)}>Fechar</Button>
            {selectedMissao?.status !== 'Concluída' && selectedMissao?.status !== 'Cancelada' && (
              <Button onClick={handleUpdateMissaoStatus} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Atualizar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mensagem Detail Modal */}
      <Dialog open={!!selectedMensagem} onOpenChange={() => setSelectedMensagem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Ticket {selectedMensagem?.codigo}</span>
              {selectedMensagem && (
                <Badge className={`${MENSAGEM_STATUS_CONFIG[selectedMensagem.status]?.color || 'bg-slate-500'} text-white`}>
                  {MENSAGEM_STATUS_CONFIG[selectedMensagem.status]?.label || selectedMensagem.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedMensagem?.instituicao?.nome || selectedMensagem?.instituicao_nome}
            </DialogDescription>
          </DialogHeader>

          {selectedMensagem && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Assunto</Label>
                <p className="font-medium">{selectedMensagem.assunto}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Categoria</Label>
                <Badge variant="outline">{selectedMensagem.categoria}</Badge>
              </div>

              {/* Histórico de mensagens */}
              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Conversa</Label>
                <div className="space-y-3 max-h-[300px] overflow-y-auto p-3 bg-slate-50 rounded-lg">
                  {selectedMensagem.historico?.map((msg, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${msg.remetente === 'admin' ? 'bg-blue-100 ml-4' : 'bg-white mr-4'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">
                          {msg.remetente === 'admin' ? 'ENTRAJUDA' : msg.user_nome || 'Instituição'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(msg.data).toLocaleString('pt-PT')}
                        </span>
                      </div>
                      <p className="text-sm">{msg.conteudo}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Responder */}
              {selectedMensagem.status !== 'Fechado' && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Responder</Label>
                  <Textarea
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                    placeholder="Escreva a sua resposta..."
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedMensagem?.status !== 'Fechado' && (
              <Button variant="outline" onClick={handleFecharMensagem} disabled={submitting} className="text-red-600">
                <XCircle className="w-4 h-4 mr-2" />
                Fechar Ticket
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedMensagem(null)}>Fechar</Button>
            {selectedMensagem?.status !== 'Fechado' && (
              <Button onClick={handleResponderMensagem} disabled={submitting || !resposta.trim()}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar Resposta
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
